import { debounce } from 'debounce';
import type { Location } from 'found';
import type React from 'react';
import { createContext, useEffect, useState } from 'react';

import { routes } from '../constants';
import { safeSessionStorage } from '../utils/safeStorage';
import useRouter from '../utils/useRouter';

interface MasterLayoutContext {
	mainContentContainer: HTMLDivElement | null;
	setMainContentContainer: (arg: HTMLDivElement | null) => void;
}

const MasterLayoutContext = createContext<MasterLayoutContext>({
	mainContentContainer: null,
	setMainContentContainer: () => null,
});

const supportedScrollRestorationPages = [routes.courseCatalog.root];

const MasterLayoutProvider: React.FC<{ children?: React.ReactNode }> = (props) => {
	const {
		router,
		match: {
			location: { pathname },
		},
	} = useRouter();
	const [mainContentContainer, setMainContentContainer] = useState<HTMLDivElement | null>(null);
	const [lastTransitionAction, setLastTransitionAction] = useState<Location['action'] | null>(null);

	useEffect(() => {
		window.addEventListener('beforeunload', () => {
			safeSessionStorage.removeItem('scrollPosition');
		});
	}, []);

	useEffect(() => {
		const handleSaveScroll = (event: Event) => {
			const target = event.target as HTMLDivElement;

			if (!supportedScrollRestorationPages.includes(pathname.substring(1))) {
				return;
			}

			const scrollPosition = safeSessionStorage.getItem('scrollPosition');
			safeSessionStorage.setItem(
				'scrollPosition',
				JSON.stringify(
					Object.assign(JSON.parse(scrollPosition || '{}'), {
						[pathname]: target.scrollTop,
					})
				)
			);
		};

		const debouncedHandleSaveScroll = debounce(handleSaveScroll, 100);

		mainContentContainer?.addEventListener('scroll', debouncedHandleSaveScroll);

		return () => {
			mainContentContainer?.removeEventListener('scroll', debouncedHandleSaveScroll);
		};
	}, [mainContentContainer, pathname]);

	useEffect(() => {
		const removeTransitionListener = router.addTransitionHook((transition) => {
			setLastTransitionAction(transition.action);
			return true;
		});

		return () => {
			removeTransitionListener();
		};
	}, [router]);

	useEffect(() => {
		if (!supportedScrollRestorationPages.includes(pathname.substring(1))) {
			return;
		}

		if (!document.body.contains(mainContentContainer)) {
			return;
		}

		if (lastTransitionAction !== 'POP') {
			return;
		}

		const scrollPosition = safeSessionStorage.getItem('scrollPosition');
		if (scrollPosition) {
			const scrollPositions = JSON.parse(scrollPosition);
			if (scrollPositions[pathname]) {
				mainContentContainer?.scrollTo(0, scrollPositions[pathname]);
			}
		}
	}, [pathname, mainContentContainer, lastTransitionAction]);

	return (
		<MasterLayoutContext.Provider value={{ mainContentContainer, setMainContentContainer }}>
			{props.children}
		</MasterLayoutContext.Provider>
	);
};

export { MasterLayoutContext, MasterLayoutProvider };
